<script setup lang="ts">
import { ref, reactive } from 'vue'
import { z } from 'zod'
import countries from '~/assets/json/dist.countries.json'
import states from '~/assets/json/dist.states.json'
import type { Country, State } from '~/types/register'
import { useUserStore } from '~/store/user'
import { LOGIN_REDIRECT_QUERY_KEY, resolveSafeRedirectTarget } from '~/utils/auth'
import { createPasswordSchema } from '~/utils/password'
import { createPhoneSchema } from '~/utils/phone'
import type { MwBffAccountSelectionResponse, MwBffSessionResponse } from '~/types/mw-bff'
import type { AxiosError } from 'axios'

const { t: $t } = useI18n()
const { termsUrl } = useLegalLinks()
const toast = useToast()
const userStore = useUserStore()
const { $mwApi } = useNuxtApp()
const localePath = useLocalePath()
const route = useRoute()

definePageMeta({ layout: 'auth' })
useSeoMeta({ title: $t('page.register.title') })

// Where to land after signup (e.g. back to the checkout the user came from).
const redirectTarget = computed(() =>
  resolveSafeRedirectTarget(route.query[LOGIN_REDIRECT_QUERY_KEY], '/')
)

// ── Wizard ────────────────────────────────────────────────────────────────
// `verify` is not one of the numbered steps: it only appears when the sign-in
// that follows the signup asks for the emailed second factor, and by then the
// account already exists — so the stepper reads as complete and the panel says
// so. See the two_factor_required branch in signInAfterRegistration().
type Step = 'account' | 'profile' | 'address' | 'verify'
const steps: { key: Exclude<Step, 'verify'>; label: string }[] = [
  { key: 'account', label: 'page.register.stepAccount' },
  { key: 'profile', label: 'page.register.stepProfile' },
  { key: 'address', label: 'page.register.stepAddress' },
]
const step = ref<Step>('account')
const currentIndex = computed(() =>
  step.value === 'verify' ? steps.length : steps.findIndex(s => s.key === step.value)
)

const isLoading = ref(false)
const isAdvancing = ref(false)
const registrationError = ref('')
const twoFactorChallengeId = ref<string | null>(null)

const StateSchema = z.object({ name: z.string(), code: z.string() })

const accountSchema = z.object({
  email: z.string().email(),
  password: createPasswordSchema($t),
  confirmPassword: z.string().min(1),
}).refine(d => d.password === d.confirmPassword, {
  path: ['confirmPassword'],
  message: $t('auth.password.mismatch'),
})

const profileSchema = z.object({
  firstName: z.string().min(2),
  lastName: z.string().min(2),
  company: z.string().optional(),
})

const addressSchema = z.object({
  country: z.object({ name: z.string(), countryCode: z.string() })
    .refine(d => d.name.length > 0, { message: $t('page.register.countryRequired') }),
  state: z.union([z.string().min(2), StateSchema]),
  city: z.string().min(2),
  zip: z.string().min(4),
  address: z.string().min(5),
  phone: createPhoneSchema($t),
})

const state = reactive({
  firstName: '',
  lastName: '',
  company: '',
  address: '',
  city: '',
  state: undefined as unknown as State | string,
  zip: '',
  country: undefined as unknown as Country,
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
})

const stateInputType = computed(() => {
  if (state.country == undefined) return 'text'
  return (states as Record<string, State[]>)[state.country.countryCode]?.length > 0 ? 'select' : 'text'
})
const statesComputed = computed(() => (states as Record<string, State[]>)[state.country.countryCode])

const loginLink = computed(() => ({
  path: localePath('/login'),
  query: {
    ...(redirectTarget.value !== '/' ? { [LOGIN_REDIRECT_QUERY_KEY]: redirectTarget.value } : {}),
    // Carry the address so a hand-off to /login never asks for it twice.
    ...(state.email.trim() ? { email: state.email.trim() } : {}),
  },
}))

watch(() => state.country, (newCountry, oldCountry) => {
  if (newCountry?.name !== oldCountry?.name) state.state = undefined as unknown as State | string
})

// ── Identifier-first: detect an existing account before collecting more ─────
const { bffFetch } = useMwBff()
const emailExists = ref(false)
// WHMCS holds the address twice — as a LOGIN (`tblusers`) and as an account's
// contact address (`tblclients`) — and they drift apart. `exists` blocks the
// signup either way; `canSignIn` is what decides whether "sign in instead" is
// honest advice or a dead end.
const emailCanSignIn = ref(false)
const checkingEmail = ref(false)
const lastCheckedEmail = ref('')
// The lookup is a WHMCS round-trip (~1.2s). Submitting must WAIT for the one
// already running rather than firing a second, and must never be blocked by it.
let emailCheckInFlight: { email: string; promise: Promise<void> } | null = null

const signInLink = computed(() => ({
  path: localePath('/login'),
  query: {
    ...(redirectTarget.value !== '/' ? { [LOGIN_REDIRECT_QUERY_KEY]: redirectTarget.value } : {}),
    email: state.email,
  },
}))

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function checkEmail(): Promise<void> {
  const email = state.email.trim()
  if (!isValidEmail(email) || email === lastCheckedEmail.value) return Promise.resolve()
  if (emailCheckInFlight?.email === email) return emailCheckInFlight.promise

  checkingEmail.value = true
  const promise = (async () => {
    try {
      const res = await bffFetch<{ exists: boolean, canSignIn: boolean }>('/whmcs/register/check-email', {
        method: 'POST',
        body: { email },
      })
      // The address may have been edited while this was in flight; a stale
      // answer must not decide anything about what is on screen now.
      if (state.email.trim() !== email) return
      emailExists.value = !!res?.exists
      emailCanSignIn.value = !!res?.canSignIn
      lastCheckedEmail.value = email
    }
    catch {
      // fail open — the final register still guards with 409
      emailExists.value = false
      emailCanSignIn.value = false
    }
    finally {
      if (emailCheckInFlight?.email === email) emailCheckInFlight = null
      checkingEmail.value = false
    }
  })()

  emailCheckInFlight = { email, promise }
  return promise
}

function focusEmail() {
  nextTick(() => document.querySelector<HTMLInputElement>('input[name="register-email"]')?.focus())
}

function useDifferentEmail() {
  emailExists.value = false
  emailCanSignIn.value = false
  state.email = ''
  lastCheckedEmail.value = ''
  focusEmail()
}

// Clear the "exists" flags as soon as the email is edited.
watch(() => state.email, (val) => {
  if (val.trim() === lastCheckedEmail.value) return
  emailExists.value = false
  emailCanSignIn.value = false
})

async function onAccountSubmit() {
  if (isAdvancing.value) return
  registrationError.value = ''
  isAdvancing.value = true
  try {
    await checkEmail()
    if (emailExists.value) return
    step.value = 'profile'
  }
  finally {
    isAdvancing.value = false
  }
}
function backToAccount() { registrationError.value = ''; step.value = 'account' }
function onProfileSubmit() { registrationError.value = ''; step.value = 'address' }
function backToProfile() { registrationError.value = ''; step.value = 'profile' }

/**
 * What the server actually said, in preference to "registration failed".
 *
 * WHMCS refuses a signup with a sentence the customer can act on ("Você não
 * digitou seu Telefone"); the BFF lifts it to `message`. This page used to drop
 * every one of them on the floor and show one generic line, so a customer with
 * a fixable problem had nothing to fix and support had nothing to go on.
 */
function describeRegistrationError(error: unknown): string {
  const axiosError = error as AxiosError<{ message?: string | string[] }>
  const raw = axiosError.response?.data?.message
  const message = Array.isArray(raw) ? raw.filter(Boolean).join('. ') : raw
  if (typeof message !== 'string' || !message.trim()) return $t('page.register.failed')
  // A bare Nest 500 says nothing a customer can use; keep the friendly line.
  if (/^internal server error$/i.test(message.trim())) return $t('page.register.failed')
  return message
}

/** Signed in and inside the console — the only successful end of a signup. */
async function finishSignedIn({ announce = true } = {}) {
  if (announce) toast.add({ title: $t('page.login.success'), color: 'success' })
  await userStore.setUserDefaultLanguage()
  await navigateTo(localePath(redirectTarget.value))
}

/** The account exists but we could not finish the session here. */
async function handOffToLogin() {
  toast.add({
    title: $t('page.register.success'),
    description: $t('page.register.accountCreatedSignIn'),
    color: 'warning',
  })
  await navigateTo(localePath(loginLink.value))
}

/**
 * The sign-in that turns a completed signup into a session.
 *
 * It can legitimately answer with something other than a session, and both
 * branches used to be treated as "login failed": a brand-new WHMCS client is
 * asked for the emailed second factor (`ValidateLogin` reports twoFactorEnabled
 * for one), so the customer was told their login had failed and dropped on
 * /login seconds after creating the account. Verified against the live WHMCS
 * install: every account created through AddClient is asked for it.
 */
async function signInAfterRegistration() {
  const email = state.email.trim()
  const result = await userStore.login(email, state.password)

  if (result && 'result' in result && result.result === 'two_factor_required') {
    twoFactorChallengeId.value = (result as { challengeId: string }).challengeId
    step.value = 'verify'
    return
  }

  // A brand-new WHMCS user owns exactly one client, so this cannot happen at
  // signup — but it must not read as a failed registration if it ever does.
  if (result && 'result' in result && result.result === 'account_selection_required') {
    await handOffToLogin()
    return
  }

  if (result && (result as MwBffSessionResponse).client) {
    await finishSignedIn()
    return
  }

  await handOffToLogin()
}

async function onTwoFactorVerified(session: MwBffSessionResponse) {
  if (session?.client) {
    await finishSignedIn()
    return
  }
  await handOffToLogin()
}

async function onTwoFactorAccountSelection(_selection: MwBffAccountSelectionResponse) {
  await handOffToLogin()
}

function onTwoFactorError(message: string) {
  registrationError.value = message
  toast.add({ title: message, color: 'error' })
}

// Final submit (address step) — register the WHMCS client, then log in.
async function onSubmitFinal() {
  try {
    registrationError.value = ''
    isLoading.value = true
    const { deviceId } = useDeviceId()

    const registrationData = {
      firstname: state.firstName.trim(),
      lastname: state.lastName.trim(),
      companyname: state.company.trim(),
      address1: state.address.trim(),
      city: state.city.trim(),
      state: typeof state.state === 'string' ? state.state.trim() : state.state.code,
      postcode: state.zip.trim(),
      country: state.country.countryCode,
      email: state.email.trim(),
      phonenumber: state.phone.trim(),
      password2: state.password,
      // Binds the session the signup ends in to this browser, exactly as a
      // normal login does.
      deviceId,
      // Browser locale so the WHMCS account is created with the visitor's
      // language (pt → portuguese-br, anything else → english).
      language: import.meta.client ? navigator.language : undefined,
    }

    const response = await $mwApi.register(registrationData)

    if (response.data.result !== 'success') {
      registrationError.value = $t('page.register.failed')
      toast.add({ title: $t('page.register.failed'), color: 'error' })
      return
    }

    toast.add({ title: $t('page.register.success'), color: 'success' })

    // A signup ENDS SIGNED IN: the API mints the session from the fact that the
    // same request created the account, so there is no second credential check
    // and no emailed factor to clear. See sessionForNewAccount in the API.
    const session = response.data as unknown as MwBffSessionResponse
    if (session?.client && session?.sessionId) {
      await userStore.applySessionData(session)
      // The signup already said "account created"; a second toast is noise.
      await finishSignedIn({ announce: false })
      return
    }

    // No session came back (a WHMCS wobble between the create and the lookup).
    // The account exists, so sign in the ordinary way — second factor included.
    try {
      await signInAfterRegistration()
    }
    catch {
      // The account was created; only the session was not. Never re-submit the
      // registration from here — that is a guaranteed 409.
      await handOffToLogin()
    }
  }
  catch (error) {
    const axiosError = error as AxiosError
    if (axiosError.response?.status === 409) {
      registrationError.value = $t('page.register.emailExists')
      toast.add({ title: $t('page.register.emailExists'), color: 'error' })
      // Send them back to the account step, and re-ask which half of WHMCS
      // holds the address so the panel there gives the right advice.
      lastCheckedEmail.value = ''
      step.value = 'account'
      focusEmail()
      void checkEmail()
    }
    else {
      const message = describeRegistrationError(error)
      registrationError.value = message
      toast.add({ title: $t('page.register.failed'), description: message, color: 'error' })
    }
  }
  finally {
    isLoading.value = false
  }
}

onMounted(() => {
  // Auto-select country from the browser locale.
  const language = navigator?.language?.split('-')
  if (language && language.length > 1) {
    const browserCountry = (countries as Country[]).find(c => c.countryCode === language[1])
    if (browserCountry) state.country = browserCountry
  }
  // A signup form's first field should be ready to type into.
  focusEmail()
})
</script>

<template>
  <div class="w-full">

    <!-- Header -->
    <div class="text-center mb-6">
      <div class="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-primary-100 dark:bg-primary-900/30 mb-3">
        <UIcon :name="step === 'verify' ? 'i-lucide-shield-check' : 'i-lucide-user-plus'" class="w-6 h-6 text-primary-600 dark:text-primary-400" />
      </div>
      <h1 class="text-2xl text-gray-900 dark:text-white font-bold">
        {{ step === 'verify' ? $t('page.register.verifyTitle') : $t('page.register.formTitle') }}
      </h1>
      <p class="text-sm text-gray-600 dark:text-gray-300 mt-1">
        {{ step === 'verify' ? $t('page.register.verifyBody', { email: state.email.trim() }) : $t('page.register.valueProp') }}
      </p>
    </div>

    <!-- Progress stepper -->
    <div class="flex items-center mb-6">
      <template v-for="(s, i) in steps" :key="s.key">
        <div class="flex items-center gap-2">
          <div
            class="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors"
            :class="currentIndex >= i
              ? 'bg-primary-500 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'"
            :aria-current="currentIndex === i ? 'step' : undefined"
          >
            <UIcon v-if="currentIndex > i" name="i-lucide-check" class="w-4 h-4" />
            <span v-else>{{ i + 1 }}</span>
          </div>
          <span
            class="text-xs font-medium hidden sm:inline"
            :class="currentIndex >= i ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'"
          >{{ $t(s.label) }}</span>
        </div>
        <div v-if="i < steps.length - 1" class="flex-1 h-px mx-2" :class="currentIndex > i ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-700'" />
      </template>
    </div>

    <!-- Error -->
    <UAlert
      v-if="registrationError"
      color="error"
      icon="i-heroicons-exclamation-triangle"
      class="mb-5 rounded-lg"
      :title="registrationError"
    />

    <!-- Step 1: Account (social + email/password) -->
    <div v-if="step === 'account'" class="space-y-5">
      <AuthSocialLoginButtons :redirect="redirectTarget" />

      <div class="flex items-center gap-3">
        <USeparator class="flex-1" />
        <span class="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">{{ $t('page.register.orSignUpWith') }}</span>
        <USeparator class="flex-1" />
      </div>

      <UForm :schema="accountSchema" :state="state" class="space-y-4" @submit="onAccountSubmit">
        <UFormField :label="$t('page.register.email')" name="email">
          <UInput v-model="state.email" name="register-email" type="email" size="xl" icon="i-heroicons-envelope" :placeholder="$t('page.register.emailDescription')" autocomplete="email" class="w-full" @blur="checkEmail">
            <template v-if="checkingEmail" #trailing>
              <UIcon name="i-lucide-loader-circle" class="w-4 h-4 animate-spin text-gray-400" />
            </template>
          </UInput>
        </UFormField>

        <!-- Existing account → guide to login (instead of going further) -->
        <div v-if="emailExists" class="rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/10 p-5 text-center space-y-3">
          <div class="inline-flex w-11 h-11 rounded-full bg-primary-100 dark:bg-primary-900/30 items-center justify-center">
            <UIcon :name="emailCanSignIn ? 'i-lucide-user-round-check' : 'i-lucide-circle-help'" class="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <p class="font-semibold text-gray-900 dark:text-white">
              {{ emailCanSignIn ? $t('page.register.emailExistsTitle') : $t('page.register.emailNoLoginTitle') }}
            </p>
            <p class="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {{ emailCanSignIn
                ? $t('page.register.emailExistsBody', { email: state.email })
                : $t('page.register.emailNoLoginBody', { email: state.email }) }}
            </p>
          </div>
          <!-- Only offer sign-in when the address is a LOGIN. An address that is
               merely an account's contact address cannot authenticate, and
               sending someone to /login with it is a guaranteed dead end. -->
          <UButton v-if="emailCanSignIn" block size="xl" :to="signInLink" trailing-icon="i-heroicons-arrow-right-20-solid" class="shadow-lg">{{ $t('page.register.signInInstead') }}</UButton>
          <UButton :block="true" :size="emailCanSignIn ? 'md' : 'xl'" :variant="emailCanSignIn ? 'ghost' : 'solid'" :color="emailCanSignIn ? 'neutral' : 'primary'" :class="emailCanSignIn ? '' : 'shadow-lg'" @click="useDifferentEmail">{{ $t('page.register.useDifferentEmail') }}</UButton>
        </div>

        <!-- Otherwise continue setting a password -->
        <template v-else>
          <AuthPasswordCreateFields
            v-model:password="state.password"
            v-model:confirm="state.confirmPassword"
          />

          <!-- `loading` is deliberately NOT the email check: a loading UButton is a
               DISABLED one, and a disabled default button makes the browser drop
               Enter and the click alike, so pressing Continue during the ~1.2s
               lookup silently did nothing. onAccountSubmit awaits it instead. -->
          <UButton type="submit" size="xl" block :loading="isAdvancing" trailing-icon="i-heroicons-arrow-right-20-solid" class="shadow-lg hover:shadow-xl transition-all">
            {{ $t('page.register.next') }}
          </UButton>
        </template>
      </UForm>

      <p v-if="!emailExists" class="text-sm text-center text-gray-600 dark:text-gray-300">
        {{ $t('page.register.formSubtitle') }}
        <NuxtLink :to="loginLink" class="text-primary-600 dark:text-primary-400 font-semibold hover:underline">
          {{ $t('page.register.formSubtitleLink') }}
        </NuxtLink>
      </p>
    </div>

    <!-- Step 2: Profile -->
    <UForm v-else-if="step === 'profile'" :schema="profileSchema" :state="state" class="space-y-4" @submit="onProfileSubmit">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <UFormField :label="$t('page.register.name')" name="firstName">
          <UInput v-model="state.firstName" size="xl" icon="i-heroicons-user" :placeholder="$t('page.register.nameDescription')" autocomplete="given-name" class="w-full" />
        </UFormField>
        <UFormField :label="$t('page.register.surname')" name="lastName">
          <UInput v-model="state.lastName" size="xl" icon="i-heroicons-user" :placeholder="$t('page.register.surnameDescription')" autocomplete="family-name" class="w-full" />
        </UFormField>
      </div>

      <UFormField :label="`${$t('page.register.company')} (${$t('page.register.optional')})`" name="company">
        <UInput v-model="state.company" size="xl" icon="i-heroicons-building-office" :placeholder="$t('page.register.companyDescription')" autocomplete="organization" class="w-full" />
      </UFormField>

      <div class="flex gap-3 pt-2">
        <UButton variant="outline" size="xl" class="flex-1" icon="i-heroicons-arrow-left-20-solid" @click="backToAccount">{{ $t('general.back') }}</UButton>
        <UButton type="submit" size="xl" class="flex-1 shadow-lg" trailing-icon="i-heroicons-arrow-right-20-solid">{{ $t('page.register.next') }}</UButton>
      </div>
    </UForm>

    <!-- Step 3: Address / billing -->
    <UForm v-else-if="step === 'address'" :schema="addressSchema" :state="state" class="space-y-4" @submit="onSubmitFinal">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <UFormField :label="$t('page.register.country')" name="country">
          <USelectMenu v-model="(state.country as any)" :items="countries" searchable :filter-fields="['name', 'countryCode']" label-key="name" size="xl" icon="i-heroicons-globe-alt" :placeholder="$t('page.register.countryDescription')" class="w-full" trailing />
        </UFormField>
        <UFormField :label="$t('page.register.state')" name="state">
          <UInput v-if="stateInputType === 'text'" v-model="state.state as string" size="xl" icon="i-heroicons-map" :placeholder="$t('page.register.stateDescription')" autocomplete="address-level1" class="w-full" />
          <USelectMenu v-else v-model="(state.state as any)" :items="statesComputed" searchable :filter-fields="['name', 'code']" label-key="name" size="xl" icon="i-heroicons-map" :placeholder="$t('page.register.stateDescription')" class="w-full" trailing />
        </UFormField>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <UFormField :label="$t('page.register.city')" name="city">
          <UInput v-model="state.city" size="xl" icon="i-heroicons-building-office-2" :placeholder="$t('page.register.cityDescription')" autocomplete="address-level2" class="w-full" />
        </UFormField>
        <UFormField :label="$t('page.register.zip')" name="zip">
          <UInput v-model="state.zip" size="xl" icon="i-heroicons-hashtag" :placeholder="$t('page.register.zipDescription')" autocomplete="postal-code" class="w-full" />
        </UFormField>
      </div>

      <UFormField :label="$t('page.register.address')" name="address">
        <UInput v-model="state.address" size="xl" icon="i-heroicons-home" :placeholder="$t('page.register.addressDescription')" autocomplete="street-address" class="w-full" />
      </UFormField>

      <UFormField :label="$t('page.register.phone')" name="phone">
        <UInput v-model="state.phone" type="tel" size="xl" icon="i-heroicons-phone" :placeholder="$t('page.register.phoneDescription')" autocomplete="tel" class="w-full" />
      </UFormField>

      <div class="flex gap-3 pt-2">
        <UButton variant="outline" size="xl" class="flex-1" icon="i-heroicons-arrow-left-20-solid" :disabled="isLoading" @click="backToProfile">{{ $t('general.back') }}</UButton>
        <UButton type="submit" size="xl" class="flex-1 shadow-lg" :loading="isLoading" trailing-icon="i-heroicons-arrow-right-20-solid">{{ $t('page.register.createAccountBtn') }}</UButton>
      </div>
    </UForm>

    <!-- Step 4 (conditional): the emailed second factor. The account already
         exists at this point — the only thing missing is the session. -->
    <AuthTwoFactorStep
      v-else-if="step === 'verify' && twoFactorChallengeId"
      :email="state.email.trim()"
      :challenge-id="twoFactorChallengeId"
      :back-label="$t('page.register.verifyLater')"
      @verified="onTwoFactorVerified"
      @account-selection="onTwoFactorAccountSelection"
      @error="onTwoFactorError"
      @back="handOffToLogin"
    />

    <!-- Terms of Service -->
    <div class="text-xs text-gray-500 dark:text-gray-400 mt-7 text-center">
      {{ $t('page.login.tosDescription') }}
      <NuxtLink v-if="termsUrl" :to="termsUrl" target="_blank" class="text-primary-600 dark:text-primary-400 font-semibold hover:underline">
        {{ $t('page.login.tos') }}
      </NuxtLink>
      <span v-else class="font-semibold">{{ $t('page.login.tos') }}</span>
    </div>

  </div>
</template>
